import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Box, Button, CircularProgress, MenuItem, Select } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router";
import AssetCard, { AssetCardProps } from "../../../../../Components/Cards/AssetCard/asset-card";
import MovieCard, { MovieCardGrid, MovieCardProps } from "../../../../../Components/Cards/MovieCard/movie-card";
import FiltersList from "../../../../../Components/Filters/FiltersList/filters-list";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../../Components/Misc/ListPageHeader";
import ToggleLayout from "../../../../../Components/Misc/ToggleLayout";
import AutocompleteSearchCatalog from "../../../../../Components/Search/AutocompleteSearchCatalog/autocomplete-search-catalog";
import AssetTable from "../../../../../Components/Table/AssetsTable/assets-table";
import TitleTable from "../../../../../Components/Table/TitlesTable/titles-table";
import { assetService, cacheService, thumbnailService } from "../../../../../Core/Api/services";
import { ThumbnailSize } from "../../../../../Core/Api/Thumbnail/thumbnail-api-service";
import { AclTypes } from "../../../../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../../../../Core/Api/Types/db-asset-object-infos";
import { MetadataFieldValueObjectTypes } from "../../../../../Core/Api/Types/metadata-field-value-object-types";
import { ObjectLayer } from "../../../../../Core/Api/Types/object-layer";
import { SearchResponse } from "../../../../../Core/Api/Types/search-response";
import { GetMetadataByTagName } from "../../../../../Core/Utils/asset-object-utils";
import { GetPattern } from "../../../../../Redux/Reducers/redux-slice-filters";
import { UserHasAcl } from "../../../../../Utils/helpers";
import { handleSelectClickObject, isItemSelectedObject } from "../../../../../Utils/Helpers/selectMode";
import useCustomSnackbar from "../../../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";
import { routePaths } from "../../../../../Router/router";

export type InventoryCrossLevelProps = {};

const InventoryCrossLevel: React.FC<InventoryCrossLevelProps> = (props) => {
  const { t } = useTranslation();
  const { ...otherProps } = props;

  const [isNew, setIsNew] = useState(true);
  const [result, setResult] = React.useState<Omit<AssetCardProps, "index">[] | Omit<MovieCardProps, "index">[]>([]);
  const [layout, setLayout] = React.useState<"grid" | "list">("list");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [layer, setLayer] = React.useState<ObjectLayer>(ObjectLayer.Asset);
  const [resultObjects, setResultObjects] = React.useState<DbAssetObjectInfos[]>([]);
  const [tableExtended, setTableExtended] = React.useState(false);
  const [isSaveLoading, setIsSaveLoading] = React.useState(false);

  const navigate = useNavigate();

  const thumbSize: ThumbnailSize = ThumbnailSize.large;
  const customerId = cacheService.GetCustomerExternalIdLabel();
  const enqueueCustomSnackbar = useCustomSnackbar();

  const userIsSuperUser = React.useMemo(() => {
    return UserHasAcl(AclTypes.aclSuperUser);
  }, [UserHasAcl]);

  const pattern = useSelector(GetPattern);

  // Outlet context used to pass props to parent DashboardLayout through react router dom
  const { selectedItems, setSelectedItems } = useOutletContext<{
    setSelectedItems: React.Dispatch<React.SetStateAction<DbAssetObjectInfos[]>>;
    selectedItems: DbAssetObjectInfos[];
  }>();

  const setDataFromSearchCallback = (data: SearchResponse) => {
    setIsNew(false);
    if (data) processResults(data);
  };

  const setOnSearchEnter = (pattern: string) => {
    setIsLoading(true);
  };

  const processResults = (rep: SearchResponse) => {
    if (rep.results !== null) {
      if (layer == ObjectLayer.Title) {
        let movieCardsData: Omit<MovieCardProps, "index">[] = rep.results.map((movie) => {
          let date = GetMetadataByTagName(movie, "productionDate")?.value;
          let movieYear = new Date(date).getFullYear();
          let thisYear: number = new Date().getFullYear();

          return {
            id: movie.id,
            to: "#",
            cover: thumbnailService.GetObjectPosterUrl(movie.id, thumbSize),
            title: movie.name ?? "",
            labelList: movieYear === thisYear ? ["NEW"] : [movieYear.toString()],
            transparentChipText:
              GetMetadataByTagName(movie, "titleType")?.value +
              (customerId !== "" ? ` (${customerId} #${movie.titleCustomerId})` : ""),
            favorite: false,
            titleCustomerId: movie.titleCustomerId,
            expand: {
              contentList: [],
              audioList: [],
              subtitleList: [],
              tagList: [],
              quality: "",
            },
            selectMode: true,
          };
        });
        setResult(movieCardsData);
        setResultObjects(rep.results);
        if (movieCardsData.length < 300) setTableExtended(true);
        else tableExtended && setTableExtended(false);
      } else {
        let assetCardsData: Omit<AssetCardProps, "index">[] = rep.results.map((obj) => ({
          id: obj.id,
          obj: obj,
          selectMode: true,
        }));
        setResult(assetCardsData);
      }

      setIsLoading(false);
    }
  };

  const onLayoutChange = (e: React.MouseEvent<HTMLElement>, value: "grid" | "list") => {
    setLayout(value);
  };

  const onLayerChange = (layer: ObjectLayer) => {
    setResult([]);
    setResultObjects([]);
    switch (layer) {
      case ObjectLayer.Asset:
      case ObjectLayer.Content:
        setLayout("list");
        break;
      default:
        setLayout("grid");
        break;
    }
    setLayer(layer);
  };

  const handleSaveAssets = React.useCallback(() => {
    if (!result.length) return;
    setIsSaveLoading(true);
    assetService.SaveObjects(result.map((x) => x.obj.id)).then((res) => {
      if (res.success) {
        enqueueCustomSnackbar(t("alert:success.assetsSaved"), "success");
      } else {
        enqueueCustomSnackbar(res.message || t("alert:error.savingAssets"), "error");
      }

      setIsSaveLoading(false);
    });
  }, [result]);

  const BrowsingAction = useMemo(
    (): React.FC => () =>
      (
        <>
          <AutocompleteSearchCatalog
            onSearchEnter={setOnSearchEnter}
            onSearchBasicCallback={setDataFromSearchCallback}
            layer={layer}
            enableSearchCallback={true}
          />
          {layout === "list" && result.length > 300 && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setTableExtended(true)}
              disabled={tableExtended || isLoading}
            >
              {t("button:getDetailedTitlesInfos")}
            </Button>
          )}
          <Select
            IconComponent={ExpandMoreRoundedIcon}
            value={layer}
            sx={{ mb: 1, width: "30%" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            displayEmpty
            onChange={(e) => onLayerChange(ObjectLayer[e.target.value])}
          >
            <MenuItem value={ObjectLayer.Asset} key={ObjectLayer.Asset}>
              {ObjectLayer.Asset}
            </MenuItem>
            <MenuItem value={ObjectLayer.Title} key={ObjectLayer.Title}>
              {ObjectLayer.Title}
            </MenuItem>
            <MenuItem value={ObjectLayer.Content} key={ObjectLayer.Content}>
              {ObjectLayer.Content}
            </MenuItem>
          </Select>
          <ToggleLayout value={layout} onChange={onLayoutChange} />
        </>
      ),
    [setOnSearchEnter, layout, onLayoutChange, setDataFromSearchCallback]
  );

  const _TitleCardList = React.useMemo(
    () =>
      layer === ObjectLayer.Title ? (
        <MovieCardGrid>
          {result?.map((movie, index) => (
            <MovieCard
              index={movie.id}
              key={movie.id}
              selected={isItemSelectedObject(movie.obj, selectedItems!)}
              selectedOnClick={selectedItems && selectedItems.length > 0 ? true : false}
              onSelectClick={handleSelectClickObject(selectedItems!, setSelectedItems!)}
              shareBtn={true}
              orderBtn={true}
              collectionBtn={true}
              {...movie}
            />
          ))}
        </MovieCardGrid>
      ) : (
        <></>
      ),
    [result, result.length, selectedItems, layer]
  );

  const _TitleCardTable = React.useMemo(
    () => (
      <TitleTable
        titles={resultObjects}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        extended={tableExtended}
      />
    ),
    [result, result.length]
  );

  const _AssetCardList = React.useMemo(
    () =>
      layer === ObjectLayer.Asset ? (
        <MovieCardGrid>
          {result.map((asset, index) => (
            <AssetCard
              obj={asset.obj!}
              key={asset.obj!.id}
              selectedOnClick={selectedItems.length > 0}
              selected={isItemSelectedObject(asset.obj!, selectedItems)}
              onCardClick={() => navigate(`${routePaths.asset.path}/${asset.obj!.id}`)}
              onSelectClick={handleSelectClickObject(selectedItems, setSelectedItems)}
            />
          ))}
        </MovieCardGrid>
      ) : (
        <></>
      ),
    [result, result.length, selectedItems, layer]
  );

  const _AssetTable = React.useMemo(
    () => (
      <AssetTable
        rows={result.map((x) => x.obj)}
        selectedRows={selectedItems.map((x) => x.id)}
        setSelectedRows={(e) =>
          setSelectedItems(result.map((x) => x.obj).filter((x) => (e as number[]).includes(x.id)))
        }
      />
    ),
    [result, result.length, selectedItems, setSelectedItems]
  );

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <ListPageHeader mb={1}>
          <ListPageHeaderLeft
            title={
              isNew
                ? t("component:listPageHeaderLeft.title.typeAnythingOnSearchBar")
                : t("component:listPageHeaderLeft.title.resultsForPattern", {
                    count: result.length,
                    context: pattern !== "" ? "pattern" : "",
                    pattern,
                  })
            }
          />
          <ListPageHeaderRight>
            {userIsSuperUser && (
              <Button
                variant="outlined"
                startIcon={
                  isSaveLoading ? <CircularProgress size={20} thickness={5} color="secondary" /> : <SaveOutlinedIcon />
                }
                onClick={handleSaveAssets}
                disabled={!result.length || isSaveLoading}
              >
                {t("button:saveAssets")}
              </Button>
            )}
            <BrowsingAction />
          </ListPageHeaderRight>
        </ListPageHeader>
        <FiltersList sx={{ mb: 4 }} layer={null} type={MetadataFieldValueObjectTypes.DbAssetObjectInfos} />
      </Box>

      {isLoading && (
        <CircularProgress
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
          }}
        />
      )}
      {!isLoading && result && result.length > 0 && (
        <>
          {layout === "grid"
            ? layer === ObjectLayer.Title
              ? _TitleCardList
              : _AssetCardList
            : layer === ObjectLayer.Title
            ? _TitleCardTable
            : _AssetTable}
        </>
      )}
    </>
  );
};

export default InventoryCrossLevel;
